/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { PDFViewer } from "@react-pdf/renderer";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "../../../../../reducers";

import useConfig, { useTenantFormOptions } from "../../../../../actions/Tenants/config/configHook";
import { device_image_types } from "../../../AddItem/components/Images";
import { getFruitTableDataLegacy } from "../../../AddItem/components/Summary/LegacyFruitTable";
import { PDFCheckFruitTables } from "./PDFCheckFruitTables";
import { PDFImages } from "./PDFImages";
import { PDFDocument } from "./PDFLayout";

import { path_summary } from "../../../../../actions/Tenants/config/constants";
import { useRouteCheck, useRouteLayer, useRouteLayerChildren } from "../../../../../hooks/useCurrentEntities";
import { getFilledArrayOrDefault } from "../../../../../utils";
import useAuthorization from "../../../../../utils/authorization";
import { fetchBlob } from "../../../../../utils/base64";
import { useTenantDefectDefinitions } from "../../../../Forms/useManagedOptionsHook";
import { Loading } from "../../../../Helper/Loading";
import LoadingProgressBar from "../../../../Helper/LoadingProgressBar";
import useCheckFlowNavigate from "../../../AddItem/useCheckFlowNavigate";
import webpToJpgBase64, { fetchBlobUrl } from "../../../image-utils";
import FullPageLayout from "../../../Layout/FullPageLayout";
import LayerBreadcrumbs from "../LayerBreadcrumbs";
import { SendReportButton } from "../SendReportButton";
import { PDFCountDownText } from "./index";
import PdfReader from "./PDFReader";
import QCStatusButton from "./QCStatusButton";

export default function PDFCheckReport() {
    const config = useConfig();
    const user = useSelector((state: RootState) => state.auth.user);
    const layer = useRouteLayer();
    const children = useRouteLayerChildren();
    const tenantsIsLoading = useSelector((state: RootState) => state.tenants.isLoading);
    const form_options = useTenantFormOptions();
    const check = useRouteCheck();
    const [printCheck, setPrintCheck] = useState<any>();
    const [logoBase64, setLogoBase64] = useState<string>("");
    const { getPrevRoute } = useCheckFlowNavigate();

    const params = useParams();
    const layer_config = config.get_layer_config(layer);

    useEffect(() => {
        const loadLogo = async () => {
            const logoPath = config?.root_config?.logo_path;
            if (!logoPath) return;
            try {
                const logoUrl = await fetchBlobUrl(logoPath);
                const base64 = await webpToJpgBase64(logoUrl);
                setLogoBase64(base64);
            } catch (e) {
                console.error("Error loading logo", e); // eslint-disable-line no-console
            }
        };
        loadLogo();
    }, [config?.root_config?.logo_path]);

    // when you navigate from a parent layer to a child layer check summary...
    // we need to give it some time to reinstate the config (e.g. when the parent doesn't have a location)
    const location = config.get_check_type_config(layer, check);
    const tenantDefectDefinitions = useTenantDefectDefinitions(layer.fruit_type);

    useEffect(() => {
        if (!layer || !check || (tenantDefectDefinitions?.length || 0) === 0 || String(check?.check_id) !== params.check_id) return;

        const all_images = [
            ...(check.images ? check.images.map((x) => ({ ...x, label: check.label })) : []),
            ...(check.avocados
                ? check.avocados
                    .flatMap((a) => a.images.filter((x) => !device_image_types.includes(x.type)))
                    .filter((x) => x.url)
                    .map((x) => ({ ...x, label: check.label }))
                : []),
        ];

        const fruitTableData = getFruitTableDataLegacy({ layer, check, fruits: check.avocados, config, user, tenantDefectDefinitions });

        const preloadImages = async () => {
            const preloadedImages = await Promise.all(
                all_images.map(async (image) => ({
                    ...image,
                    url: await webpToJpgBase64(image.url),
                }))
            );
            setPrintCheck({ ...check, label: layer.label, table_data: fruitTableData, all_images: preloadedImages });
        };

        preloadImages();
    }, [layer?.id, layer?.label, check?.check_id, check?.updated, tenantDefectDefinitions, params.check_id,
        check?.images?.map((img) => img.image_id).join(","),
        check?.avocados?.flatMap((a) => a.images.map((img) => img.image_id)).join(",")]);

    const [pdfBlob, setPdfBlob] = useState<Blob>();
    const onRender = async ({ blob }) => {
        if (!pdfBlob || pdfBlob.size !== blob.size) {
            setPdfBlob(blob);
        }
    };

    const report_config_value = location?.value;
    // * If you want to enable the SendReportButton a pdf config with the same value as check.location should be set
    const report_config = config.get_pdf_report(layer, report_config_value as string);

    const draft_report_key = `report_eye_${report_config_value}_generated`;
    const draft_report = layer?.[draft_report_key];

    const auth = useAuthorization();
    const canSendReport = report_config?.show_send_email_button || auth.userBelongsToOneOfTeams(report_config?.teams_that_can_send);


    useEffect(() => {
        // * fetch the blob if the draft report is set to send the active draft report
        const fetchAndSetBlob = async () => {
            try {
                const blob = await fetchBlob(draft_report);
                setPdfBlob(blob);
            } catch (error) {
                console.error("Error fetching the blob:", error); // eslint-disable-line no-console
                setPdfBlob(undefined);
            }
        };

        if (draft_report) fetchAndSetBlob();
    }, [draft_report]);
    const [isDelayingRender, setIsDelayingRender] = useState(true);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const dependenciesReady = layer && check && printCheck && location && layer_config;
    useEffect(() => {
        // Only render when everything is finished, with 2.5s margin for setting up internal states
        if (dependenciesReady) {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Wait 2.5s after all dependencies are ready before allowing render
            timeoutRef.current = setTimeout(() => {
                setIsDelayingRender(false);
                timeoutRef.current = null;
            }, 2500);
        }

        // Cleanup on unmount or dependency change
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [dependenciesReady]);

    if (isDelayingRender) {
        return <Loading />;
    }

    if (!dependenciesReady) {
        return <Loading />;
    }

    const title = report_config?.title || "Check report";

    const PrintReport = ({ onRender }) => {
        const logo_path = logoBase64; // Use preloaded base64 logo
        const layer_fields = getFilledArrayOrDefault(location.summary_layer_fields_pdf).map((i) => i.set_value({
            config,
            layer,
            check, // * To be used in check tables and layer overview
            fruit: null, // * Fruit list is not available on overview screen
            children, // * children is only available for layer index your are viewing
            form_options, // * used to translate form values to labels
        }));
        return (
            <>
                <PDFDocument onRender={onRender} title={`${title} ${layer?.label}`}>
                    <PDFCheckFruitTables
                        header={printCheck.table_data[0]}
                        rows={printCheck.table_data[1]}
                        footer={printCheck.table_data[2]}
                        fields={layer_fields}
                        tenantDefectDefinitions={tenantDefectDefinitions}
                        defect_groups={getFilledArrayOrDefault(report_config?.defect_groups, location.defect_groups)}
                        check={printCheck}
                        logo_path={logo_path}
                        header_title={title}
                    />

                    <PDFImages images={printCheck.all_images} />
                </PDFDocument>
            </>
        );
    };

    const isLoading: boolean = !printCheck || tenantsIsLoading;

    return (
        <FullPageLayout title={`${layer_config.text} ${layer.label}`} breadcrumbs={<LayerBreadcrumbs check={check} reportTitle={`${report_config_value} Report`} />}>
            <div className="d-flex ms-auto mb-3 flex-column flex-md-row align-items-start align-items-md-center" css={css`gap: 0.5rem;`}>
                <QCStatusButton
                    pdfConfig={report_config}
                    pdfBlob={pdfBlob}
                    configValue={report_config_value as string}
                    draftReportKey={draft_report_key}
                    qcStatusField={`qc_status_${report_config_value as string}`}
                    editUrl={getPrevRoute(`/layer/${layer.id}/add-check/${check.check_id}/${path_summary}`)} // * always go back to check summary
                    backUrl={getPrevRoute(`/layer/${layer.id}/add-check/${check.check_id}/${path_summary}`)} // * always go back to check summary
                />

                {canSendReport && <SendReportButton layerId={layer.id} blob={pdfBlob} config_value={report_config_value as string} status={check.status} />}
            </div>

            {isLoading ? (<LoadingProgressBar text={PDFCountDownText.slice(0, 4)} />
            ) : (
                <div className="max-w-100">
                    {!pdfBlob && !draft_report && <LoadingProgressBar text={PDFCountDownText} startIndex={4} />}


                    {!draft_report && <div css={css`margin: auto; max-widht: 100%;`} >
                        <PDFViewer style={{ width: "100%", height: "1200px", opacity: pdfBlob ? 1 : 0 }}>
                            <PrintReport onRender={onRender} />
                        </PDFViewer>
                    </div>
                    }
                    {draft_report && <PdfReader pdfUrl={draft_report} id={layer.label} />}
                </div>
            )}
        </FullPageLayout>
    );
}
